import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Switch,
  FormControlLabel,
  CircularProgress,
  Breadcrumbs,
  Link,
  Grid,
  Chip,
  Stack,
  Tooltip,
  Container,
  Card,
  CardContent
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CodeIcon from '@mui/icons-material/Code';
import Requester from '../../../utils/requester';
import { useNotification } from '../../../context/NotificationContext';

export default function CommandCreate() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showNotification } = useNotification();
  
  const [saving, setSaving] = useState(false);
  const [newAlias, setNewAlias] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});
  
  // Initialize with default values for a new command
  const [command, setCommand] = useState({
    command_prefix: '',
    response_pattern: '',
    command_delay: 0,
    global_command_delay: 0,
    is_active: true,
    skip_timeout_admins: true,
    is_reply: false,
    only_admins: false,
    aliases: []
  });

  // Handle form input changes
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCommand(prevCommand => ({
      ...prevCommand,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Handle number input changes
  const handleNumberChange = (e) => {
    const { name, value } = e.target;
    // Convert to number and ensure it's not negative
    const numValue = Math.max(0, parseInt(value) || 0);
    setCommand(prevCommand => ({
      ...prevCommand,
      [name]: numValue
    }));
  };
  
  // Handle adding new alias
  const handleAddAlias = () => {
    if (newAlias.trim() === '') return;
    
    // Check if alias exceeds 50 characters
    if (newAlias.trim().length > 50) {
      showNotification(t('commands_list.alias_too_long') || 'Alias cannot exceed 50 characters', {
        type: 'error',
        autoHideDuration: 3000
      });
      return;
    }
    
    // Check if we've reached the maximum of 2 aliases
    if ((command.aliases || []).length >= 2) {
      showNotification(t('commands_list.max_aliases_error'), {
        type: 'error',
        autoHideDuration: 3000
      });
      return;
    }
    
    setCommand(prevCommand => ({
      ...prevCommand,
      aliases: [...(prevCommand.aliases || []), newAlias.trim()]
    }));
    setNewAlias('');
  };
  
  // Handle removing alias
  const handleRemoveAlias = (aliasToRemove) => {
    setCommand(prevCommand => ({
      ...prevCommand,
      aliases: prevCommand.aliases.filter(alias => alias !== aliasToRemove)
    }));
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Reset any previous errors
    setFieldErrors({});
    
    if (!command.command_prefix) {
      showNotification(t('commands_list.command_prefix_required'), {
        type: 'error',
        autoHideDuration: 3000
      });
      return;
    }
    
    if (command.command_prefix.length > 50) {
      setFieldErrors({
        command_prefix: ['Command prefix cannot exceed 50 characters']
      });
      showNotification(t('commands_list.command_prefix_too_long') || 'Command prefix cannot exceed 50 characters', {
        type: 'error',
        autoHideDuration: 3000
      });
      return;
    }
    
    if (!command.response_pattern) {
      showNotification(t('commands_list.response_pattern_required'), {
        type: 'error',
        autoHideDuration: 3000
      });
      return;
    }
    
    if (command.response_pattern.length > 500) {
      setFieldErrors({
        response_pattern: ['Response text cannot exceed 500 characters']
      });
      showNotification(t('commands_list.response_pattern_too_long') || 'Response text cannot exceed 500 characters', {
        type: 'error',
        autoHideDuration: 3000
      });
      return;
    }
    
    // Validate all aliases length
    const longAlias = (command.aliases || []).find(alias => alias.length > 50);
    if (longAlias) {
      showNotification(t('commands_list.aliases_too_long') || 'All aliases must be 50 characters or less', {
        type: 'error',
        autoHideDuration: 3000
      });
      return;
    }
    
    setSaving(true);
    
    try {
      // Create extra_data with aliases
      const extraData = {
        aliases: Array.isArray(command.aliases) ? [...command.aliases] : []
      };
      
      const payload = {
        command_prefix: command.command_prefix,
        response_pattern: command.response_pattern,
        is_active: command.is_active,
        skip_timeout_admins: command.skip_timeout_admins,
        command_delay: command.command_delay,
        global_command_delay: command.global_command_delay,
        is_reply: command.is_reply,
        only_admins: command.only_admins,
        extra_data: extraData,
        command_class: "TextCommandPlugin"
      };
      
      const response = await Requester.post('command/create/', payload);
      
      if (response.ok) {
        showNotification(t('commands_list.command_created_successfully'), {
          type: 'success',
          autoHideDuration: 3000
        });
        
        // Navigate back to command list after successful creation
        setTimeout(() => navigate('/dashboard/commands'), 1500);
      } else {
        console.error('Failed to create command');
        
        // Try to parse the error response
        try {
          const errorData = await response.json();
          
          // Check if the error response contains field-specific errors
          if (typeof errorData === 'object' && errorData !== null) {
            // Set field errors for display in the form
            setFieldErrors(errorData);
            
            // Create a message with all error details
            const errorMessages = Object.entries(errorData)
              .map(([field, errors]) => `${field}: ${Array.isArray(errors) ? errors.join(', ') : errors}`)
              .join('\n');
            
            showNotification(t('commands_list.command_created_error') + ': ' + errorMessages, {
              type: 'error',
              autoHideDuration: 6000
            });
          } else {
            // Generic error
            showNotification(t('commands_list.command_created_error'), {
              type: 'error',
              autoHideDuration: 3000
            });
          }
        } catch (parseError) {
          // If we can't parse the error response JSON
          showNotification(t('commands_list.command_created_error'), {
            type: 'error',
            autoHideDuration: 3000
          });
        }
      }
    } catch (error) {
      console.error('Error creating command:', error);
      showNotification(t('commands_list.command_created_error'), {
        type: 'error',
        autoHideDuration: 3000
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', pb: 8 }}>
      <Container maxWidth="lg" sx={{ pt: 4 }}>
        {/* Header Section */}
        <Box sx={{ mb: 4 }}>
          <Breadcrumbs aria-label="breadcrumb" sx={{ mb: 2 }}>
            <Link
              underline="hover"
              color="inherit"
              to="/dashboard"
              component={RouterLink}
              sx={{ '&:hover': { color: 'primary.main' } }}
            >
              {t('dashboard_navigation.home')}
            </Link>
            <Link
              underline="hover"
              color="inherit"
              to="/dashboard/commands"
              component={RouterLink}
              sx={{ '&:hover': { color: 'primary.main' } }}
            >
              {t('dashboard_navigation.commands')}
            </Link>
            <Typography color="primary">{t('commands_list.create_command')}</Typography>
          </Breadcrumbs>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <CodeIcon sx={{ fontSize: 32, color: 'primary.main' }} />
            <Typography variant="h4" fontWeight="500">
              {t('commands_create.create_new_command')}
            </Typography>
          </Box>
        </Box>

        <Card
          elevation={0}
          sx={{
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <form onSubmit={handleSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Tooltip title={t('commands_create.command_prefix_tooltip')} arrow>
                    <TextField
                      fullWidth
                      required
                      label={t('commands_create.command_prefix_label')}
                      name="command_prefix"
                      value={command.command_prefix || ''}
                      onChange={handleChange}
                      margin="normal"
                      helperText={fieldErrors.command_prefix ? fieldErrors.command_prefix[0] : t('commands_create.command_prefix_help', { count: command.command_prefix.length })}
                      error={Boolean(fieldErrors.command_prefix)}
                      InputProps={{ 
                        inputProps: { maxLength: 50 } 
                      }}
                    />
                  </Tooltip>
                </Grid>
                
                <Grid item xs={12}>
                  <Tooltip title={t('commands_create.response_text_tooltip')} arrow>
                    <TextField
                      fullWidth
                      required
                      label={t('commands_create.response_text_label')}
                      name="response_pattern"
                      value={command.response_pattern || ''}
                      onChange={handleChange}
                      margin="normal"
                      multiline
                      rows={4}
                      helperText={fieldErrors.response_pattern ? fieldErrors.response_pattern[0] : t('commands_create.response_text_help', { count: command.response_pattern.length })}
                      error={Boolean(fieldErrors.response_pattern)}
                      InputProps={{ 
                        inputProps: { maxLength: 500 } 
                      }}
                    />
                  </Tooltip>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Tooltip title={t('commands_create.command_delay_tooltip')} arrow>
                    <TextField
                      fullWidth
                      label={t('commands_create.command_delay_label')}
                      name="command_delay"
                      type="number"
                      value={command.command_delay || 0}
                      onChange={handleNumberChange}
                      margin="normal"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  </Tooltip>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Tooltip title={t('commands_create.global_command_delay_tooltip')} arrow>
                    <TextField
                      fullWidth
                      label={t('commands_create.global_command_delay_label')}
                      name="global_command_delay"
                      type="number"
                      value={command.global_command_delay || 0}
                      onChange={handleNumberChange}
                      margin="normal"
                      InputProps={{ inputProps: { min: 0 } }}
                    />
                  </Tooltip>
                </Grid>
                
                <Grid item xs={12}>
                  <Tooltip title={t('commands_create.command_aliases_tooltip')} arrow>
                    <Typography variant="subtitle1" sx={{ mb: 1 }}>{t('commands_create.command_aliases_label')} (max 2)</Typography>
                  </Tooltip>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 1 }}>
                    <TextField
                      label={t('commands_create.new_alias_label')}
                      value={newAlias}
                      onChange={(e) => setNewAlias(e.target.value)}
                      sx={{ mr: 1, flexGrow: 1 }}
                      inputProps={{ maxLength: 50 }}
                      helperText={`${newAlias.length}/50 (${2 - (command.aliases || []).length} slots remaining)`}
                    />
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={handleAddAlias}
                      sx={{
                        backgroundColor: 'primary.main',
                        '&:hover': {
                          backgroundColor: 'primary.dark',
                        },
                        mt: 1
                      }}
                    >
                      {t('commands_create.add_button')}
                    </Button>
                  </Box>
                  
                  <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
                    {(command.aliases || []).map((alias, index) => (
                      <Chip
                        key={index}
                        label={alias}
                        onDelete={() => handleRemoveAlias(alias)}
                        sx={{
                          bgcolor: 'grey.700',
                          color: 'white',
                          '&:hover': { bgcolor: 'grey.800' }
                        }}
                      />
                    ))}
                  </Stack>
                </Grid>
                
                <Grid item xs={12}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Tooltip title={t('commands_create.is_active_tooltip')} arrow>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={command.is_active || false}
                              onChange={handleChange}
                              name="is_active"
                            />
                          }
                          label={t('commands_create.is_active_label')}
                        />
                      </Tooltip>
                    </Grid>
                    
                    <Grid item xs={12} md={3}>
                      <Tooltip title={t('commands_create.skip_timeout_admins_tooltip')} arrow>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={command.skip_timeout_admins || false}
                              onChange={handleChange}
                              name="skip_timeout_admins"
                            />
                          }
                          label={t('commands_create.skip_timeout_admins_label')}
                        />
                      </Tooltip>
                    </Grid>
                    
                    <Grid item xs={12} md={3}>
                      <Tooltip title={t('commands_create.is_reply_tooltip')} arrow>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={command.is_reply || false}
                              onChange={handleChange}
                              name="is_reply"
                            />
                          }
                          label={t('commands_create.is_reply_label')}
                        />
                      </Tooltip>
                    </Grid>
                    
                    <Grid item xs={12} md={3}>
                      <Tooltip title={t('commands_create.only_admins_tooltip')} arrow>
                        <FormControlLabel
                          control={
                            <Switch
                              checked={command.only_admins || false}
                              onChange={handleChange}
                              name="only_admins"
                            />
                          }
                          label={t('commands_create.only_admins_label')}
                        />
                      </Tooltip>
                    </Grid>
                  </Grid>
                </Grid>
                
                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button 
                      component={RouterLink} 
                      to="/dashboard/commands"
                      variant="outlined"
                    >
                      {t('common.cancel')}
                    </Button>
                    <Button 
                      type="submit" 
                      variant="contained" 
                      color="primary"
                      disabled={saving}
                    >
                      {saving ? <CircularProgress size={24} /> : t('commands_create.create_button')}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
} 